import { AsyncPipe, NgIf, TitleCasePipe, UpperCasePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { User } from 'app/models';
import {
  ActivityApi,
  ExportTypes,
} from 'app/modules/dashboard/api/activity.api';
import { ActivityInformations } from 'app/modules/dashboard/models/activity.model';
import { ModalService } from 'app/services/modal.service';
import { SkeletonModule } from 'primeng/skeleton';
import { BehaviorSubject, Subscription } from 'rxjs';

import { WatchCommentList } from '../../../scenic/components/watchCommentsList/watchCommentList.component';
import { WatchLikeList } from '../../../scenic/components/watchLikeList/watchLikeList.component';
import { TrainingLogActivityEditModalComponent } from '../../modals/ts-activity-edit-modal/ts-activity-edit-modal.component';
import { TrainingLogShareModalComponent } from '../../modals/ts-share-activity/ts-share-activity.component';
import { ActivityMode } from 'app/modules/dashboard/models/timeline.model';
import { IconComponent } from '@kinomap-dev/ng-ui-lib';
import { DeleteActivityModalComponent } from 'app/modules/browse/modals/deleteActivityModal/deleteActivityModal.component';

@Component({
  imports: [
    NgIf,
    SkeletonModule,
    AsyncPipe,
    UpperCasePipe,
    TitleCasePipe,
    TranslateModule,
    IconComponent,
  ],
  selector: 'app-tl-actions-block',
  standalone: true,
  styleUrls: ['./tl-actions-block.component.scss'],
  template: `
    <div id="actions" class="block">
      <div class="top">
        <span
          ><span class="icon icon-kinomap"></span
          >{{ 'training_log.actions.kinomap_activity' | translate }}</span
        >
      </div>

      <div>
        @if (isAuthorUser) {
          <a class="rounded-btn" (click)="editActivity()">
            <span class="icon icon-new-edit"></span>
            <span class="btn-label">{{
              'training_log.actions.btn.edit' | translate | titlecase
            }}</span>
          </a>
          <a class="rounded-btn sm-btn" (click)="shareActivity()">
            <span class="icon icon-new-share"></span>
            <span class="btn-label">{{
              'training_log.actions.btn.share' | translate | titlecase
            }}</span>
          </a>
        }

        <a class="rounded-btn sm-btn">
          <span class="icon icon-download"></span>
          <span class="btn-label">{{
            'training_log.actions.btn.download' | translate | titlecase
          }}</span>

          <!-- Download menu -->
          <span class="dropdown-menu">
            @for (exportType of exportTypesArray; track $index) {
              <span
                class="dropdown-option"
                (click)="downloadExport(exportType)"
                >{{ exportType | uppercase }}</span
              >
            }
          </span>
        </a>

        <!-- DELETE 🗑️ -->
        <a class="rounded-btn sm-btn" (click)="openDeleteModal()">
          <kin-icon name="trash" size="20" />
          <span class="btn-label">{{
            'modal.deleteBtn' | translate | titlecase
          }}</span>
        </a>

        <ng-template #actionBtnSkeleton>
          <p-skeleton shape="circle" width="4rem" height="4rem"></p-skeleton>
        </ng-template>
      </div>

      <div class="divider"></div>

      <div class="bottom">
        <!-- 💛  LIKES -->
        <span class="counter">
          <ng-container *ngIf="liked | async; else strokeLikeIcon">
            <span class="icon icon-like-full" (click)="likeIconClick()"></span>
          </ng-container>

          <ng-template #strokeLikeIcon>
            <span class="icon icon-like" (click)="likeIconClick()"></span>
          </ng-template>
          <span class="value">{{ likesCount | async }}</span>
          <span class="label" (click)="likesCountClick()">{{
            'training_log.actions.counter.likes' | translate | titlecase
          }}</span>
        </span>

        <!-- 💬 COMMENTS -->
        <span class="counter" (click)="commentsClick()">
          <ng-container *ngIf="commented | async; else commentedIcon">
            <span class="icon icon-comment-full"></span>
          </ng-container>
          <ng-template #commentedIcon>
            <span class="icon icon-comment"></span>
          </ng-template>

          <span class="value">{{ commentsCount | async }}</span>
          <span class="label">{{
            'training_log.actions.counter.comments' | translate | titlecase
          }}</span>
        </span>
      </div>
    </div>
  `,
})
export class TrainingLogActionsBlockComponent implements OnInit {
  private modal: ModalService = inject(ModalService);
  private activityApi: ActivityApi = inject(ActivityApi);
  private translate: TranslateService = inject(TranslateService);

  @Input() activity: ActivityInformations;
  @Input() currentUser: User;
  @Output() delete: EventEmitter<void> = new EventEmitter<void>();
  @Output() statusChange: EventEmitter<string> = new EventEmitter<string>();

  authorInfos: any = {
    avatarUrl: null,
    countryCode: null,
    id: null,
    username: null,
  };

  commented: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  comments: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  commentsCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  isAuthorUser: boolean = false;
  liked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // 💛
  likeProgress: boolean = false;
  likesCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  modalRenderedSubscription: Subscription;
  subscriptions: Subscription[] = [];
  activityMode = ActivityMode;
  exportTypesArray: ExportTypes[] = ['gpx', 'tcx', 'pwx'];

  commentsClick(focusCommentId: number = null) {
    if (!this.currentUser) return;
    const modalClass = 'list-comments__modal';
    this.modal.display(modalClass);
    this.modalRenderedSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchCommentList);
    });

    if (this.modal.dialogComponent instanceof WatchCommentList) {
      const component = this.modal.dialogComponent as WatchCommentList;
    } else {
      this.modalRenderedSubscription = this.modal.onModalRendered.subscribe(
        (component: WatchCommentList) => {
          component.commentsCount = this.commentsCount.getValue();
          component.user = this.currentUser;
          if (focusCommentId) component.focusCommentId = focusCommentId;
          component.title = this.activity.name;
          component.commentType = 'activity';
          component.sessionId =
            this.activity.sessions[this.activity.sessions_count - 1].id;
          component.scrollContainerSelector = `.${modalClass} div.p-dialog-content`;
          component.initList(this.activity.id);
          component.commentsCountEvent.subscribe((res) => {
            this.commentsCount.next(res);
          });
          component.commentAdded.subscribe((res: any) => {
            const comment: any = {
              body: res.body,
              created_at: new Date().getTime() / 1000,
              parent_id: null,
              reply_count: 0,
              user: this.currentUser,
            };
            this.comments.next([...this.comments.getValue(), comment]);
          });
          component.deleteCommentOutput.subscribe((commentId: number) => {
            const i = this.comments
              .getValue()
              .findIndex((com) => com.id === commentId);
            const newComments = [...this.comments.getValue()];
            newComments.splice(i, 1);
            this.comments.next(newComments);
          });
        }
      );
    }
  }

  /**
   * Downloads the activity in the specified format.
   * @param format The format of the export.
   */
  downloadExport(format: ExportTypes): void {
    const { name, id } = this.activity;
    this.subscriptions.push(
      this.activityApi
        .getGpx(id.toString(), format)
        .subscribe((response: Blob) => {
          const blob = new Blob([response], {
            type: 'application/octet-stream',
          });
          const url = URL.createObjectURL(blob);
          const filename = `${name}--${id}.${format}`;
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
        })
    );
  }

  /**
   * � Edit Activity
   */
  editActivity() {
    const modalClass: string = 'activity-edit__modal';
    this.modal.display(modalClass);
    this.modalRenderedSubscription?.unsubscribe();
    setTimeout(() => {
      this.modal.setDialogComponent(TrainingLogActivityEditModalComponent);
    });

    if (
      this.modal.dialogComponent instanceof
      TrainingLogActivityEditModalComponent
    ) {
      return;
    }

    this.modalRenderedSubscription = this.modal.onModalRendered.subscribe(
      (dialogComponent: TrainingLogActivityEditModalComponent) => {
        if (this.activity) {
          dialogComponent.status = this.activity.status;
          dialogComponent.activityId = this.activity.id.toString();
          dialogComponent.updateStatus.subscribe((res) => {
            this.statusChange.emit(res);
            this.modal.hide();
          });
          dialogComponent.cancelClick.subscribe(() => {
            this.modal.hide();
          });
        }
      }
    );
  }

  /**
   * 💛 LIKE CLICK
   */
  likeIconClick() {
    if (!this.likeProgress) {
      this.likeProgress = true;
      if (!this.liked.getValue()) {
        this.activityApi
          .like(
            this.activity.id.toString(),
            this.activity.sessions[
              this.activity.sessions_count - 1
            ].id.toString()
          )
          .subscribe((res) => {
            this.likesCount.next(res.data.likes_count);
            this.likeProgress = false;
          });
      } else {
        this.activityApi
          .dislike(
            this.activity.id.toString(),
            this.activity.sessions[
              this.activity.sessions_count - 1
            ].id.toString()
          )
          .subscribe((res) => {
            this.likesCount.next(res.data.likes_count);
            this.likeProgress = false;
          });
      }
      this.liked.next(!this.liked.getValue());
    }
  }

  /**
   * 💛⏲️ LIKE COUNTER CLICK
   */
  likesCountClick() {
    const likeModalClass: string = 'list-like__modal';
    this.modal.display(likeModalClass);
    this.modalRenderedSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchLikeList);
    });

    if (this.modal.dialogComponent instanceof WatchLikeList) {
      const component = this.modal.dialogComponent as WatchLikeList;
    } else {
      this.modalRenderedSubscription = this.modal.onModalRendered.subscribe(
        (component: WatchLikeList) => {
          component.title = this.activity.name;
          component.liked = this.liked;
          component.likeCount = this.likesCount;
          component.scrollContainerSelector = `.${likeModalClass} div.p-dialog-content`;
          component.currentUser = this.currentUser;
          component.type = 'activity';
          component.videoId = this.activity.id;
          component.sessionId =
            this.activity.sessions[
              this.activity.sessions_count - 1
            ].id.toString();

          this.subscriptions = [
            ...this.subscriptions,
            component.likeClick.subscribe(() => {
              this.likeIconClick();
            }),
          ];
        }
      );
    }
  }

  /**
   * 📨 SHARE ACTIVITY
   */
  shareActivity() {
    const sharedModalClass: string = 'shared__modal';
    this.modal.display(sharedModalClass);
    this.modalRenderedSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(TrainingLogShareModalComponent);
    });

    if (this.modal.dialogComponent instanceof TrainingLogShareModalComponent) {
      const component = this.modal
        .dialogComponent as TrainingLogShareModalComponent;
    } else {
      this.modalRenderedSubscription = this.modal.onModalRendered.subscribe(
        (component: TrainingLogShareModalComponent) => {
          component.activityId = this.activity.id;
          component.sessionId =
            this.activity.sessions[this.activity.sessions_count - 1].id;
          this.subscriptions = [...this.subscriptions];
        }
      );
    }
  }

  openDeleteModal(): void {
    const deleteModalClass: string = 'delete_activity-modal';
    this.modal.display(deleteModalClass);
    this.modalRenderedSubscription?.unsubscribe();
    this.modal.setTitle(this.translate.instant('modal.activity-delete.title'));
    setTimeout(() => {
      this.modal.setDialogComponent(DeleteActivityModalComponent);
      this.modal.onModalRendered.subscribe(
        (component: DeleteActivityModalComponent) => {
          component.cancel.subscribe(() => {
            this.modal.hide();
          });
          component.confirm.subscribe(() => {
            this.activityApi
              .deleteActivity(this.activity.id.toString())
              .subscribe(() => {
                this.modal.hide();
                this.delete.emit();
              });
          });
        }
      );
    });
  }

  ngOnInit(): void {
    // 💛 update
    this.likesCount.next(
      this.activity.sessions[this.activity.sessions_count - 1].likes_count
    );
    this.liked.next(
      this.activity.sessions[this.activity.sessions_count - 1].has_liked
    );
    // 💬 update
    this.commentsCount.next(
      this.activity.sessions[this.activity.sessions_count - 1].comments_count
    );
    this.commented.next(
      this.activity.sessions[this.activity.sessions_count - 1].has_commented
    );

    /* pour définir si l'utilisteur connecté est l'auteur de la video. */
    if (
      this.activity.sessions[this.activity.sessions_count - 1].user_id ===
      this.currentUser.id
    ) {
      this.isAuthorUser = true;
    } else {
      this.isAuthorUser = false;
    }
  }
}
